
import { Helmet } from "react-helmet";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SeoAnalysisTool from "@/components/SeoAnalysisTool";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle, AlertTriangle, Mail, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PageTransition from "@/components/PageTransition";

const SeoTool = () => {
  // SEO tool benefits
  const benefits = [
    "Comprehensive analysis of on-page SEO factors",
    "Technical SEO evaluation",
    "Content quality assessment",
    "Mobile-friendliness check",
    "Page speed insights",
    "Backlink profile overview",
    "Competitor comparison",
    "Actionable recommendations"
  ];

  return (
    <PageTransition>
      <Helmet>
        <title>Free SEO Analysis Tool | Test Your Website SEO | DesertSEO</title>
        <meta name="description" content="Use our free SEO analysis tool to check your website's search engine optimization. Get instant insights and recommendations to improve your rankings." />
        <meta name="keywords" content="SEO analysis tool, website SEO checker, SEO audit tool, SEO analyzer, free SEO tool" />
      </Helmet>

      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-r from-desert-sky/10 to-desert-cactus/10">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium rounded-full bg-desert-sky/10 text-desert-sky">
              Free SEO Tools Suite
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Check Your Website's <span className="text-gradient bg-sky-gradient">SEO Health</span>
            </h1>
            <p className="text-xl text-foreground/70 mb-8 leading-relaxed">
              Get an instant analysis of your website's SEO performance and discover opportunities for improvement with our free tools.
            </p>
          </div>
        </div>
      </section>
      
      {/* SEO Tool Section */}
      <section className="section-padding bg-background">
        <div className="page-container">
          <SeoAnalysisTool />
        </div>
      </section>
      
      {/* Benefits Section */}
      <section className="section-padding bg-muted/50">
        <div className="page-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium rounded-full bg-desert-cactus/10 text-desert-cactus">
                SEO Tool Benefits
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                What Our <span className="text-gradient bg-cactus-gradient">Analysis</span> Includes
              </h2>
              <p className="text-lg mb-8 text-foreground/80">
                Our comprehensive SEO analysis tool evaluates multiple aspects of your website to provide a holistic view of your search performance.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-desert-cactus flex-shrink-0" />
                    <span>{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glassmorphism dark:glassmorphism-dark rounded-xl p-8"
            >
              <h3 className="text-2xl font-bold mb-4">Want a More In-Depth Analysis?</h3>
              <p className="mb-6 text-foreground/80">
                While our free tool provides valuable insights, our team can perform a comprehensive manual audit that dives deeper into your website's SEO performance and competitive landscape.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-desert-cactus mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Advanced Technical Analysis</p>
                    <p className="text-sm text-foreground/70">Our experts will thoroughly examine your site's technical foundation, identifying issues that automated tools often miss.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-desert-cactus mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Competitor Benchmarking</p>
                    <p className="text-sm text-foreground/70">We'll analyze your top competitors to identify opportunities and develop strategies to outrank them.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-desert-cactus mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Custom Action Plan</p>
                    <p className="text-sm text-foreground/70">Receive a prioritized roadmap of recommendations tailored to your specific business goals and industry.</p>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <Button asChild className="bg-desert-gradient hover:opacity-90 w-full">
                  <Link to="/contact">
                    Request a Custom Audit
                    <ArrowUpRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Weekly Report Section */}
      <section className="section-padding bg-background">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium rounded-full bg-desert-sky/10 text-desert-sky">
                Weekly SEO Reports
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Stay Updated with <span className="text-gradient bg-sky-gradient">Weekly Reports</span>
              </h2>
              <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
                Schedule automated weekly SEO reports for your website to track performance over time and stay on top of new opportunities.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="glassmorphism dark:glassmorphism-dark rounded-xl p-6"
              >
                <div className="rounded-full w-12 h-12 flex items-center justify-center bg-muted mb-4">
                  <Calendar className="w-6 h-6 text-desert-sunset" />
                </div>
                <h3 className="text-xl font-bold mb-3">How It Works</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="bg-desert-sunset/10 text-desert-sunset w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                    <span>Complete the form with your contact information after analyzing your site</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="bg-desert-sunset/10 text-desert-sunset w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                    <span>Select your preferred day of the week for report delivery</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="bg-desert-sunset/10 text-desert-sunset w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                    <span>Add additional team members who should receive the report</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="bg-desert-sunset/10 text-desert-sunset w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">4</span>
                    <span>Receive your first report instantly, with weekly updates to follow</span>
                  </li>
                </ul>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="glassmorphism dark:glassmorphism-dark rounded-xl p-6"
              >
                <div className="rounded-full w-12 h-12 flex items-center justify-center bg-muted mb-4">
                  <Mail className="w-6 h-6 text-desert-sky" />
                </div>
                <h3 className="text-xl font-bold mb-3">Email Delivery System</h3>
                <p className="mb-4">
                  Our automated email system delivers your scheduled reports reliably every week. Here's how we handle your data:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-desert-cactus flex-shrink-0 mt-0.5" />
                    <span>Reports are sent using a secure AWS SES email delivery service</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-desert-cactus flex-shrink-0 mt-0.5" />
                    <span>Your contact information is stored securely and never shared with third parties</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-desert-cactus flex-shrink-0 mt-0.5" />
                    <span>Each report includes fresh data from our SEO analysis engine</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <span>You can unsubscribe at any time with a single click in any report email</span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="section-padding bg-muted/50">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium rounded-full bg-desert-sunset/10 text-desert-sunset">
              Frequently Asked Questions
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Common <span className="text-gradient bg-sunset-gradient">Questions</span> About Our SEO Tool
            </h2>
          </div>
          
          <div className="max-w-3xl mx-auto grid gap-8">
            {[
              {
                question: "Is this SEO analysis tool really free?",
                answer: "Yes, our basic SEO analysis tool is completely free to use. There are no hidden fees or requirement to purchase additional services. We offer this as a valuable resource to help businesses understand their SEO performance."
              },
              {
                question: "How accurate are the results?",
                answer: "Our tool provides a solid overview of your SEO performance. While automated tools cannot replace the nuanced analysis of an SEO professional, the insights you'll receive are based on industry best practices and current search engine algorithms."
              },
              {
                question: "How often should I run an SEO analysis?",
                answer: "We recommend running an analysis at least once a month, as well as after making significant changes to your website. Search algorithms and competitive landscapes change regularly, so ongoing monitoring is essential."
              },
              {
                question: "What should I do with the results?",
                answer: "The analysis will provide recommendations for improvement. You can implement these yourself, or if you'd prefer professional assistance, our team is available to help develop and execute a comprehensive SEO strategy based on the findings."
              },
              {
                question: "How do the weekly email reports work?",
                answer: "After analyzing your site, you can schedule weekly reports by providing your contact information and selecting your preferred delivery day. Our system will automatically analyze your site each week and send a detailed report to all designated email addresses."
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-background border border-border rounded-xl p-6 shadow-sm"
              >
                <h3 className="text-xl font-bold mb-3">{faq.question}</h3>
                <p className="text-foreground/70">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-desert-sunset to-desert-canyon">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready for Professional SEO Services?
            </h2>
            <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
              After using our free tool, take the next step toward transforming your search performance with our expert SEO team.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Button
                asChild
                size="lg"
                className="bg-white text-desert-sunset hover:bg-white/90"
              >
                <Link to="/contact">
                  Contact Us Today
                  <ArrowUpRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10"
              >
                <Link to="/case-studies">
                  See Our Results
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </PageTransition>
  );
};

export default SeoTool;
